@if (isOpen) {
  <section
    #panel
    class="ap"
    role="dialog"
    aria-labelledby="ap-h"
    tabindex="-1"
    (keydown)="onKeydown($event)"
  >
    <span class="ap-caret" aria-hidden="true"></span>

    <header class="ap-top">
      <h2 id="ap-h">Alerts</h2>
      @if (state() === 'rows') {
        <span class="ap-n">{{ notifications().length }}</span>
      }
      <button
        class="ap-close"
        type="button"
        aria-label="Close alerts"
        (click)="close()"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.6"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </header>

    <div class="ap-body">
      @switch (state()) {
        @case ('loading') {
          <p class="ap-one" role="status">Loading your alerts...</p>
        }
        @case ('failed') {
          <!-- Not the same as having none: a member with an unread counselor
               reply must not be told there is nothing waiting. -->
          <div class="ap-one" role="alert">
            <p>Your alerts did not load. Anything waiting is still there.</p>
            <button type="button" class="ap-link" (click)="retry()">
              Try again
            </button>
          </div>
        }
        @case ('empty') {
          <!-- Nothing in the product writes an alert yet, so the empty state
               promises nothing. -->
          <!-- Walkthrough 09-29 #9 v3 / #11, approved: a small spot
               illustration above the one line. -->
          <div class="ap-empty">
            <!-- The Goals Report spot-art style (front-door.art.ts): one
                   colour in three layers, 14%, 34% and solid, on a 56px grid. -->
            <svg
              class="ap-art"
              width="72"
              height="72"
              viewBox="0 0 56 56"
              aria-hidden="true"
            >
              <rect
                x="6"
                y="6"
                width="44"
                height="44"
                rx="10"
                fill="currentColor"
                fill-opacity=".14"
              />
              <path
                d="M28 13c-6.4 0-10.5 4.8-10.5 10.8v7.7L13.5 37h29l-4-5.5v-7.7C38.5 17.8 34.4 13 28 13z"
                fill="currentColor"
                fill-opacity=".34"
              />
              <rect
                x="26"
                y="9"
                width="4"
                height="6"
                rx="2"
                fill="currentColor"
              />
              <rect
                x="23.5"
                y="39.5"
                width="9"
                height="5"
                rx="2.5"
                fill="currentColor"
              />
              <rect
                x="22"
                y="23"
                width="12"
                height="3.5"
                rx="1.75"
                fill="currentColor"
                fill-opacity=".34"
              />
            </svg>
            <p class="ap-one"><b>No alerts yet.</b></p>
          </div>
        }
        @default {
          @if (waiting().length) {
            <p class="ap-grp" id="ap-grp-w">Waiting on you</p>
            <ul class="ap-rows" aria-labelledby="ap-grp-w">
              @for (row of waiting(); track row.id) {
                <ng-container
                  *ngTemplateOutlet="rowTpl; context: { $implicit: row }"
                ></ng-container>
              }
            </ul>
          }
          @if (informational().length) {
            <p class="ap-grp" id="ap-grp-i">For information</p>
            <ul class="ap-rows" aria-labelledby="ap-grp-i">
              @for (row of informational(); track row.id) {
                <ng-container
                  *ngTemplateOutlet="rowTpl; context: { $implicit: row }"
                ></ng-container>
              }
            </ul>
          }
        }
      }
    </div>
  </section>
}

<ng-template #rowTpl let-row>
  <li class="ap-row" [attr.data-kind]="row.kind.toLowerCase()">
    <!-- Walkthrough 09-29 #9 v3 / #11, approved: an icon for the row's
         kind, in place of the dot. -->
    <span class="ap-ic" aria-hidden="true">
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        @switch (row.kind.toLowerCase()) {
          @case ('reply') {
            <!-- Someone answered: a speech bubble. -->
            <path
              d="M21 12a8 8 0 0 1-8 8H8l-4 3v-5.6A8 8 0 0 1 13 4a8 8 0 0 1 8 8z"
            />
          }
          @case ('action') {
            <!-- A school or organization responded: the building. -->
            <path d="M2.5 9.5L12 4l9.5 5.5" />
            <path d="M4.5 9.5v9M19.5 9.5v9M9.5 18.5v-5h5v5M2.5 18.5h19" />
          }
          @case ('result') {
            <!-- Something finished: the check. -->
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12.5l2.7 2.7L16 9.8" />
          }
          @default {
            <!-- A standing agent noticed something: the trend line. -->
            <path d="M3 16.5l5.5-5.5 4 4L20 7" />
            <path d="M14.5 7H20v5.5" />
          }
        }
      </svg>
    </span>
    <span class="ap-m">
      <b>{{ row.title }}</b>
      @if (row.body) {
        <span class="ap-body-t">{{ row.body }}</span>
      }
      <span class="ap-meta">
        <span [title]="row.createdAt | date: 'medium'">{{
          relativeTime(row.createdAt)
        }}</span>
        <!-- Label and path are both present or both absent by construction. -->
        @if (row.destinationPath) {
          <button type="button" class="ap-link" (click)="goTo(row)">
            {{ row.destinationLabel }}
          </button>
        }
      </span>
    </span>
    <button
      type="button"
      class="ap-x"
      [attr.aria-label]="'Dismiss: ' + row.title"
      (click)="dismiss(row)"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.6"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  </li>
</ng-template>
